import { call, put, select } from 'redux-saga/effects';
import { Action } from '@reduxjs/toolkit';
import { hasPermissionsSaga } from 'features/permissions/sagas/hasPermissionsSaga';
import { PermissionTypes } from 'features/permissions/types';
import { questionAnswerTypeResetRequested } from 'features/qa/actions';
import { questionsAnonymized } from 'features/qa/qaSlice';
import { selectNotificationsByScope } from 'features/notifications/notificationsSlice';
import { closeActionNotifications } from 'features/notifications/thunks/closeActionNotifications';
import { NotificationId } from 'features/notifications/types';
import { FeatureFlag } from 'features/feature-set/types';

interface QAPermissionChecks {
  canAnswer: boolean;
  canAnswerOrModerate: boolean;
  canSeeQuestionsAuthor: boolean;
  canAccessQA: boolean;
}

export function* checkQAPermissionsSaga() {
  const canAnswer: boolean = yield call(hasPermissionsSaga, PermissionTypes.answerQA);

  const canAnswerOrModerate: boolean = yield call(
    hasPermissionsSaga,
    [PermissionTypes.answerQA, PermissionTypes.moderateQA],
    { mode: 'any' }
  );

  const canSeeQuestionsAuthor: boolean = yield call(
    hasPermissionsSaga,
    PermissionTypes.seeQuestionsAuthor
  );

  const canAccessQA: boolean = yield call(hasPermissionsSaga, PermissionTypes.accessQa);

  return {
    canAnswer,
    canAnswerOrModerate,
    canSeeQuestionsAuthor,
    canAccessQA,
  };
}

export function* handleRoleChangeQAPermissionsSaga() {
  const permissionChecks: QAPermissionChecks = yield call(checkQAPermissionsSaga);

  if (!permissionChecks.canAccessQA) {
    const questionNotificationIds: NotificationId[] = yield select(
      selectNotificationsByScope,
      FeatureFlag.qa
    );

    if (questionNotificationIds.length > 0) {
      const cleanupNotificationsThunk: Action = yield call(
        closeActionNotifications,
        questionNotificationIds
      );
      yield put(cleanupNotificationsThunk);
    }

    return;
  }

  if (!permissionChecks.canAnswer) {
    yield put(questionAnswerTypeResetRequested());
  }

  if (!permissionChecks.canAnswerOrModerate) {
    const questionNotificationIds: NotificationId[] = yield select(
      selectNotificationsByScope,
      'question'
    );

    if (questionNotificationIds.length > 0) {
      const cleanupNotificationsThunk: Action = yield call(
        closeActionNotifications,
        questionNotificationIds
      );
      yield put(cleanupNotificationsThunk);
    }
  }

  if (!permissionChecks.canSeeQuestionsAuthor) {
    yield put(questionsAnonymized());
  }
}
